import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GraduationCap, Banknote, Users, Sparkles, ChevronRight, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import PackagesSection from "../components/PackagesSection";
import { API } from "../context/AuthContext";

const IMG = (p) => `${API}/static/illustrations/${p}`;

const Perk = ({ icon: Icon, title, text }) => (
  <div className="card-hover bg-white border border-[#E5EBE7] rounded-2xl p-7">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0FDF4] ring-1 ring-[#16A34A]/20 mb-4">
      <Icon className="h-5 w-5 text-[#16A34A]" />
    </span>
    <h3 className="font-display text-xl font-semibold text-[#0A2518] mb-1">{title}</h3>
    <p className="text-[#4B5563] leading-relaxed">{text}</p>
  </div>
);

export default function ForEducators() {
  const { t } = useTranslation();
  return (
    <div data-testid="educators-page">
      <section className="relative overflow-hidden py-20">
        <span className="geo-shape bg-[#16A34A]/20 h-72 w-72 -top-20 -left-20 drift-slow" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="pill"><Briefcase className="h-3.5 w-3.5" /> {t("forEducators.pill")}</span>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl font-black text-[#0A2518] tracking-tight leading-tight">{t("forEducators.title")}</h1>
            <p className="mt-6 text-lg text-[#4B5563]">{t("forEducators.intro")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-full h-12 px-7 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="educators-cta-register">
                <Link to="/register?role=educator">{t("forEducators.ctaRegister")} <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-7 border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]">
                <Link to="/login">{t("common.alreadyAccount")}</Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden border border-[#E5EBE7] shadow-xl">
              <img src={IMG("educators_hero.png")} alt="" className="w-full h-[520px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F3F7F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-[#0A2518]">{t("forEducators.section2Title")}</h2>
            <p className="mt-3 text-[#4B5563] max-w-2xl mx-auto">{t("forEducators.section2Sub")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Perk icon={Sparkles} title={t("forEducators.p1Title")} text={t("forEducators.p1Text")} />
            <Perk icon={Banknote} title={t("forEducators.p2Title")} text={t("forEducators.p2Text")} />
            <Perk icon={Users} title={t("forEducators.p3Title")} text={t("forEducators.p3Text")} />
            <Perk icon={GraduationCap} title={t("forEducators.p4Title")} text={t("forEducators.p4Text")} />
            <Perk icon={Briefcase} title={t("forEducators.p5Title")} text={t("forEducators.p5Text")} />
            <Perk icon={ChevronRight} title={t("forEducators.p6Title")} text={t("forEducators.p6Text")} />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-bold text-[#0A2518]">{t("forEducators.finalTitle")}</h2>
          <p className="mt-4 text-[#4B5563]">{t("forEducators.finalText")}</p>
          <Button asChild className="mt-8 rounded-full h-14 px-8 bg-[#16A34A] hover:bg-[#15803D] text-white">
            <Link to="/register?role=educator">{t("forEducators.finalCta")}</Link>
          </Button>
        </div>
      </section>

      <PackagesSection ctaPath="/register?role=educator" />
    </div>
  );
}
